/**
 * @module app.track.ProfileService
 */
import ngeoProfileD3Elevation from 'ngeo/profile/d3Elevation.js';
import olFeature from 'ol/Feature.js';
import olGeomPoint from 'ol/geom/Point.js';
import olLayerVector from 'ol/layer/Vector.js';
import olSourceVector from 'ol/source/Vector.js';
import olStyleIcon from 'ol/style/Icon.js';
import olStyleStyle from 'ol/style/Style.js';
import {distance as olDistance} from 'ol/coordinate';
import {Z_INDEX} from "../constants";
import {getMatchingStyles, smallScreen} from "../utils";


/**
 * @type {string}
 */
const LIGHT_PROFILE_SELECTOR = '#light_track_profile';

/**
 * @type {string}
 */
const DETAILED_PROFILE_SELECTOR = '#detailed_track_profile';

/**
 * @type {string}
 */
const DETAILED_PROFILE_CLASS = '.detailed-track-profile';

/**
 * Returns transform as array
 * The same code can be found in D3 private methods to parse svg transform values
 * @see https://github.com/d3/d3-interpolate/blob/master/src/transform/parse.js
 * @param {string} transform
 * @suppress {checkTypes}
 */
function getTranslation(transform) {
  // Create a dummy g for calculation purposes only. This will never
  // be appended to the DOM and will be discarded once this function
  // returns.
  /** @type {SVGTransformList} */
  const g = document.createElementNS("http://www.w3.org/2000/svg", "g");

  // Set the transform attribute to the provided string value.
  g.setAttributeNS(null, "transform", transform);

  // consolidate the SVGTransformList containing all transformations
  // to a single SVGTransform of type SVG_TRANSFORM_MATRIX and get
  // its SVGMatrix.
  const matrix = g.transform.baseVal.consolidate().matrix;

  g.remove();

  // As per definition values e and f are the ones for the translation.
  return [matrix.e, matrix.f];
}


function translateByY(svg, graph, height) {
  const transform = getTranslation(graph.attr("transform"));
  transform[1] += height;
  graph.attr("transform", () => `translate(${transform[0]},${transform[1]})`);
  svg.attr("height", parseFloat(svg.attr("height")) + height);
}


function scaleModifier(scope) {
  // Flattened array of [xMax,ratioYOverX].
  const ratiosXY = [0, 0.5, 0.33, 0.2, 0.1, 0.05];
  const ratiosMinDist = [0, 2000, 5000, 20000, 100000, Infinity];
  console.assert(ratiosXY.length === ratiosMinDist.length);
  // the height of a poi label in pixel (from the line to the top of the label)
  const poiHeight = 200;

  return function(x, y, width, height) {
    let i;

    // find the altitude of the highest poi
    let poiAltitude = -Infinity;
    if (scope && scope.trackPois && scope.trackPois['features']) {
      const pois = scope.trackPois['features'];
      for (i = 0; i < pois.length; i++) {
        poiAltitude = Math.max(pois[i]['properties']['z'], poiAltitude);
      }
    }

    /**
     * @type {Array.<number>}
     */
    const xDomain = x.domain();

    /**
     * @type {Array.<number>}
     */
    let yDomain = y.domain();

    // Add padding to Y domain
    const padding = (yDomain[1] - yDomain[0]) * 0.1;
    y.domain([yDomain[0] - padding, yDomain[1] + padding]);
    yDomain = y.domain();

    const xMin = xDomain[0];
    const xMax = xDomain[1];
    const yMin = yDomain[0];
    let yMax = yDomain[1];

    // Search for the best XY ratio
    let yDomainLength = 0;
    i = 0;
    let reverse = false;
    // The algorithm is:
    // - advance to reach the expected ratio;
    // - if the computed ydomain would not be large enough, reverse direction;
    // - go backwards until a good ydomain is found;
    // - if none is found keep the ydomain values from d3.
    while (true) {

      if (!reverse && xMax > ratiosMinDist[i]) {
        // advance in the ratio table
        ++i;
        continue;
      }

      const ratioYOverX = ratiosXY[i];
      const xResolution = (xMax - xMin) / width;
      yDomainLength = ratioYOverX * xResolution * height;

      const yResolution = yDomainLength / height;
      // the 'altitude' of the top of the poi label
      const poiLabelAltitude = poiAltitude + yResolution * poiHeight;
      if (poiLabelAltitude > yMax) {
        // the altitude of the poi label is higher than the altitude
        // of the line; update the y domain
        yMax = poiLabelAltitude;
        y.domain([yMin, yMax]);
      }
      if (i > 0 && yDomainLength < yMax - yMin) {
        // not enough space with this ratio to show all the
        // y values. Go back to try another ratio.
        reverse = true;
        --i;
        continue;
      }

      break;
    }

    // Apply XY ratio
    if (yDomainLength) {
      const yMean = (yMax + yMin) / 2;
      const yHalfDomain = yDomainLength / 2;
      y.domain([yMean - yHalfDomain, yMean + yHalfDomain]);
    }

    // Lower bound for y-axis
    if (y.domain()[0] < 0) {
      const shift = 0 - y.domain()[0];
      // For yLowerBound=0 and y0=-50, y is shifted 50m up
      y.domain([0, y.domain()[1] + shift]);
    }
  };
}

/**
 * @param {boolean} xAxisUnit True if units should be added to ticks
 * @return {ngeox.profile.ProfileFormatter}
 */
function formatterFactory(xAxisUnit) {
  const customLocale = d3.formatDefaultLocale({
    'decimal': '.',
    'thousands': "'",
    'grouping': [3]
  });
  const floatFormat = customLocale.format(',.1f');
  const decimalFormat = customLocale.format(',.2d');

  return {
    /** @return {string} */
    xhover: function(dist, units) {
      return floatFormat(dist) + ' ' + units;
    },
    yhover: function(ele, units) {
      return decimalFormat(ele) + ' ' + units;
    },
    xtick: function(dist, units) {
      const str = floatFormat(parseFloat(dist.toPrecision(3)));
      return xAxisUnit ? str + ' ' + units : str;
    },
    ytick: function(ele, units) {
      return decimalFormat(ele);
    }
  };
}

function createProfileCallbacks(appMapService, cacheBustService) {
  const profileHoverGeometry = new olGeomPoint([0, 0]);

  const profileHoverVector = new olLayerVector({
    'name': 'profile hover',
    'altitudeMode': 'clampToGround',
    'visible': false,
    'type': 'track_profile_hover',
    zIndex: Z_INDEX.UP,
    // Workaround https://github.com/AnalyticalGraphicsInc/cesium/issues/3452
    'style3d': new olStyleStyle({
      image: new olStyleIcon(/** @type {olx.style.IconOptions} */ ({
        src: cacheBustService.addSuffix('img/pointer_cross_cut.png')
      }))
    }),
    style: new olStyleStyle({
      image: new olStyleIcon(/** @type {olx.style.IconOptions} */ ({
        src: cacheBustService.addSuffix('img/pointer_cross2.png')
      }))
    }),
    source: new olSourceVector({
      features: [new olFeature({
        geometry: profileHoverGeometry
      })]
    })
  });
  appMapService.map.addLayer(profileHoverVector);

  const outCallback = () => {
    profileHoverVector.setVisible(false);
    appMapService.map.render();
  };

  /**
   * @param {ol.Coordinate} point
   */
  const hoverCallback = (point) => {
    // An item in the list of points given to the profile.
    const coordinates = [point[0], point[1]];
    profileHoverGeometry.setCoordinates(coordinates);
    profileHoverVector.setVisible(true); // no notify if already visible
    appMapService.map.render();
  };

  return {
    outCallback,
    hoverCallback
  };
}

/**
 * @return {string}
 */
function detailedProfileStyleDefs() {
  const styles = getMatchingStyles(new RegExp(`${DETAILED_PROFILE_CLASS} .*`));
  const stylesWithoutPrefix = styles.map(style => style.split(DETAILED_PROFILE_CLASS)[1].trim());
  return stylesWithoutPrefix.join('\n');
}

class ProfileService {
  /**
   * @param {angularGettext.Catalog} gettextCatalog
   * @param {string} apiUrl API url prefix.
   * @param {app.AuthenticationService} appAuthenticationService
   * @param {app.MapService} appMapService
   * @param {app.track.printService} appTrackPrintService
   * @param {app.track.service} appTrackService
   * @param {app.track.filterService} appTrackFilterService
   * @param {app.CacheBustService} appCacheBustService
   * @ngInject
   */
  constructor(gettextCatalog, apiUrl, appAuthenticationService,
    appMapService, appTrackPrintService, appTrackService,
    appTrackFilterService, appCacheBustService) {

    /**
     * @private
     */
    this.apiUrl_ = apiUrl;

    /**
     * @private
     */
    this.authentication_ = appAuthenticationService;

    /**
     * @private
     */
    this.appTrackPrintService_ = appTrackPrintService;

    /**
     * @private
     */
    this.appTrackService_ = appTrackService;

    /**
     * @type {app.track.filterService}
     * @export
     */
    this.appTrackFilterService = appTrackFilterService;

    /**
     * @type {app.MapService}
     * @private
     */
    this.appMapService_ = appMapService;

    /**
     * @type {string}
     * @private
     */
    this.saveTrackConfigErrorMsg_ = gettextCatalog.getString('Error when saving track config');

    const {outCallback, hoverCallback} = createProfileCallbacks(this.appMapService_, appCacheBustService);

    this.distanceExtractor = /** @type {function(Object): number} */ ((item) => item[3]);

    this.zExtractor = /** @type {function(Object): number} */ ((item) => item[2]);

    this.linesConfiguration = {
      'lineZ': {
        zExtractor: this.zExtractor
      }
    };

    /**
     * @param {Object} d
     * @param {number=} opt_value
     * @return {number}
     */
    const zFn = (d, opt_value) => {
      if (opt_value !== undefined) {
        d['properties']['z'] = opt_value;
      }
      return d['properties']['z'];
    };

    this.poiExtractor = {
      dist: function(d) {
        return d['properties']['dist'];
      },
      z: zFn,
      id: function(d) {
        return d['properties']['sort'];
      },
      title: function(d) {
        const properties = d['properties'];
        let str;
        if (properties['title'].length > 20) {
          str = properties['title'].substring(0, 17) + '...';
        } else {
          str = properties['title'];
        }
        return str + ', ' + Math.round(zFn(d)) + ' m';
      },
      sort: function(d) {
        return d['properties']['sort'];
      }
    };

    /**
     * @type {ngeox.profile.ProfileOptions}
     * @private
     */
    this.lightProfileOptions_ = /** @type {ngeox.profile.ProfileOptions} */ ({
      light: false,
      lightXAxis: true,
      scaleModifier: scaleModifier(undefined),
      poiLabelAngle: -90,
      formatter: formatterFactory(true),
      hoverCallback: hoverCallback,
      outCallback: outCallback,
      distanceExtractor: this.distanceExtractor,
      linesConfiguration: this.linesConfiguration,
      poiExtractor: undefined
    });

    /**
     * @type {ngeox.profile.ProfileOptions}
     * @private
     */
    this.detailedProfileOptions_ = /** @type {ngeox.profile.ProfileOptions} */ ({
      light: false,
      lightXAxis: false,
      scaleModifier: scaleModifier(this),
      poiLabelAngle: -90,
      formatter: formatterFactory(false),
      styleDefs: detailedProfileStyleDefs(),
      hoverCallback: hoverCallback,
      outCallback: outCallback,
      distanceExtractor: this.distanceExtractor,
      linesConfiguration: this.linesConfiguration,
      poiExtractor: this.poiExtractor
    });

  }

  /**
   * @export
   */
  get lightProfile() {
    return $(LIGHT_PROFILE_SELECTOR).get(0);
  }


  /**
   * @export
   */
  get detailedProfile() {
    return $(DETAILED_PROFILE_SELECTOR).get(0);
  }

  /**
   * @export
   */
  get lightProfileWidth() {
    return this.lightProfile ? this.lightProfile.clientWidth : 0;
  }


  /**
   * @export
   */
  get detailedProfileWidth() {
    return this.detailedProfile ? this.detailedProfile.clientWidth : 0;
  }


  /**
   * @param {boolean=} opt_detailed
   */
  refreshD3Profile(opt_detailed) {
    if ((!opt_detailed && this.lightProfileWidth === 0) ||
        (opt_detailed && this.detailedProfileWidth === 0)) {
      // hidden, drawing would result in [0px, 0px] svg.
      return;
    }

    if (!this.appTrackService_.selectedTrack) {
      return;
    }

    const geojson = this.appTrackService_.selectedTrack;
    let datum = geojson['properties']['profile'];
    if (!Array.isArray(datum) || datum.length === 0) {
      datum = [[0, 0, 0, 0], [0, 0, 0, 0]];
    }

    const light = !opt_detailed;
    let dom, options;
    if (light) {
      dom = this.lightProfile;
      options = this.lightProfileOptions_;
    } else {
      dom = this.detailedProfile;
      options = this.detailedProfileOptions_;
    }
    // calculate profile for multiline track
    if (Array.isArray(datum[0][0])) {
      const data = [];
      let distance = 0;
      datum.forEach((segment, segmentIdx) => {
        segment.forEach((coordinates, coordIdx) => {
          // copy coordinates
          const coord = coordinates.slice();
          // apply prev distance to all points in segment to have correct total length
          coord[3] += distance;
          // calculate distance for first point of segment after pause
          if (coordIdx === 0 && segmentIdx !== 0) {
            const prevSegment = datum[segmentIdx - 1];
            const prevPoint = prevSegment[prevSegment.length - 1];
            const pauseDistance = olDistance(coord, prevPoint);
            distance = prevPoint[3] + distance + pauseDistance;
            coord[3] = distance;
          }
          // pass last segment point with height=null to start new segment and height=0 for correct graph
          if (coordIdx === 0 && segmentIdx !== 0) {
            const stopPoint = [0, 0, null, coord[3]];
            data.push(stopPoint, [0, 0, 0, coord[3]], stopPoint);
          }
          data.push(coord);
          // pass first segment point with height = null to have correct height graph
          if (coordIdx === segment.length - 1 && segmentIdx !== datum.length - 1) {
            data.push([0, 0, null, coord[3]]);
          }
        });
      });
      datum = data;
    }
    const profile = ngeoProfileD3Elevation(options, options);
    d3.select(dom).datum(datum).call(profile);

    if (this.appTrackService_.selectedTrackPois && options.poiExtractor) {
      profile.showPois(this.appTrackService_.selectedTrackPois['features']);
    }
  }

  /**
   * @export
   * @param {boolean=} opt_print If true the printing console is also opened.
   */
  showDetailedProfile(opt_print) {
    let print = !!opt_print;
    const element = $('#detailed_profile_modal');
    const gElement = $('#detailed_profile_modal_content');
    if (!smallScreen()) {
      // only allow moving of the modal on desktop resolutions
      $(gElement).draggable();
    }

    let printStyle = null;

    element.one('shown.bs.modal', () => {
      if (!printStyle) {
        printStyle = document.createElement('link');
        printStyle.rel = 'stylesheet';
        printStyle.type = 'text/css';
        printStyle.media = 'print';
        printStyle.href = 'css/print_detailed_profile.css';

        // Add the print css to the head and display profile
        document.head.appendChild(printStyle);
        if (print) {
          print = false;
          this.appTrackPrintService_.printDetailedProfile();
        }
      }
    });

    element.one('hidden.bs.modal', () => {
      // Remove the print css from the head
      document.head.removeChild(printStyle);
      printStyle = null;
    });

    element.modal('show');
  }

  /**
   * @export
   */
  printDetailedProfile() {
    this.appTrackPrintService_.printDetailedProfile();
  }


  /**
   * @export
   */
  exportDetailedProfile() {
    const svg = d3.select('#detailed_track_profile svg');
    const graph = svg.select("g");

    // Add title
    const titleHeight = 40;
    translateByY(svg, graph, titleHeight);
    const textNode = graph.append("text")
      .attr("x", 0)
      .attr("y", -titleHeight / 2)
      .attr("text-anchor", "start")
      .style("font-size", "14px")
      .style("font-weight", "700")
      .attr("fill", "#e01826")
      .text(this.appTrackService_.selectedTrack.properties.name);

    // Get string representation of svg
    const profileAsText = svg
      .attr('version', 1.1)
      .attr('xmlns', 'http://www.w3.org/2000/svg')
      .attr('xmlns:xlink', 'http://www.w3.org/1999/xlink')
      .node().parentNode.innerHTML;

    // Remove title
    translateByY(svg, graph, -titleHeight);
    textNode.remove();

    const blob = new Blob([profileAsText], {
      type: 'image/svg+xml'
    });
    window['saveAs'](blob, 'myProfile.svg');
  }
}

const name = 'appTrackProfileService';
export default angular.module(name, []).service(name, ProfileService);
